import Notes from "components/notes";
import OfficialFeedbackCard from "components/OfficialFeedbackCard";
import PageTitle from "components/pageTitle";
import { useToast } from "contexts/ToastContext";
import TwoColumnLayout from "layouts/TwoColumnLayout";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoadingPageIcon } from "utils/constant";

import { StatusChip } from "components/StatusChip";
import { useAuth } from "contexts/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import {
    createLegalResearchRequest,
    getCommitteeLegalResearchRequest,
    getLegalResearchRequest,
    updateLegalResearchRequest,
    updateLegalResearchRequestCommittee,
} from "services/legalResearchService";
import { SingleLegalRequest } from "types";
import { LEGAL_RESEARCH_REQUEST_STATUS } from "utils/constantValues";
import { formatDate } from "utils/helpers";
import ResearchTopicCard from "./fragments/researchTopicCard";
import HeadOfOAGBLegalRequest from "./headOfOAGBLegalRequest";
import DirectorLegalResearch from "./directorLegalReseach";
import CoordinatorConductLegalResearch from "./coordinatorLegalResearch";
import ProsecutorLegalResearch from "./prosecutorLegalResearch/prosecutorLegalResearch";
import RegistrarLegalResearch from "./fragments/RegistrarLegalResearch";
import { getStakeholder } from "services/getEmployees";
import FileView from "components/fileView";
import ConfirmationModal from "components/ConfirmationModal1";
import AssignProsecutorModal from "./fragments/assignProsecutorModal";

import { expandRoles } from "utils/authRules";
const SingleCommitteeConductLegalResearch = () => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const contextData = useAuth();
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();
    const [data, setData] = useState({
        id: "",
        isLoading: true,
        requested_by: "",
        requested_by_id: "",
        reason: "",
        status: "",
        forward_status: "",
        letter_reference_number: "",
        research_topic: "",
        request_summary: "",
        created_at: "",
        request_letter: null as File | null,
        draft_instruction: null as File | null,
        remark_for_stakeholder: "",
        research_completion_letter_file: "",
        messenger_name: "",
        date: "",
        respondbacks: [{
            id: "",
            file: null,
            file_details: null,
            given_by: "",
            role: "",
            note: "",
            feedback_type: "",
            status: "",
            created_at: "",
            updated_at: "",
            research_request: ""
        }],
    });

    const [openConfirmModal, setOpenConfirmModal] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [isAssignProsecutorModalOpen, setIsAssignProsecutorModalOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState<'forward' | 'forwardToProsecutor' | 'coordinatorForward' | null>(null);

    const { id } = useParams<{ id: string }>();

    useEffect(() => {
        const fetchData = async () => {
            try {
                let response = await getCommitteeLegalResearchRequest(id || "", contextData.token || contextData?.user?.access_token || "");

                let user = null;
                if (response.requested_by_id && response.requested_by !== "internal") {
                    try {
                        user = await getStakeholder(response.requested_by_id, contextData.token || contextData?.user?.access_token || "");
                    } catch (err) {
                        console.error("Error fetching stakeholder:", err);
                        showToast(t("FailedToFetchStakeholder"), "error");
                    }
                }

                setData({
                    isLoading: false,
                    ...response,
                    requested_by_user: user,
                });

                if (response.prosecutor_assignments && response.prosecutor_assignments.length > 0) {
                    setData((prev) => ({
                        ...prev,
                        status: LEGAL_RESEARCH_REQUEST_STATUS.research_committee_underway.value,
                        forward_status: LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value,
                    }));
                }
            } catch (error) {
                console.error("Error fetching legal research request:", error);
                showToast(t("FailedToFetchLegalResearchRequest"), "error");
                setData((prev) => ({ ...prev, isLoading: false }));
            }
        };

        fetchData();
    }, [id]);

    const [formState, setFormState] = useState({
        reason: "",
        letter_reference_number: "",
        research_topic: "",
        request_summary: "",
        request_letter: null as File | null,
        draft_instruction: null as File | null,
        respondbacks: null,
    });

    const fileInputRefRequestLetter = useRef<HTMLInputElement>(null);
    const fileInputRefDraftInstruction = useRef<HTMLInputElement>(null);

    const handleForwardStatus = async () => {
        setConfirmLoading(true);

        const updateData = new FormData();

        if (data.status === LEGAL_RESEARCH_REQUEST_STATUS.pending.value) {
            updateData.append("status", LEGAL_RESEARCH_REQUEST_STATUS.pending.value);
            updateData.append("forward_status", LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value);
        }
        else if (data.status === LEGAL_RESEARCH_REQUEST_STATUS.research_committee_respondbacked.value) {
            updateData.append("status", LEGAL_RESEARCH_REQUEST_STATUS.research_committee_acceptd.value);
            updateData.append("forward_status", LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director.value);
        }

        try {
            const response = await updateLegalResearchRequestCommittee(data.id, updateData, contextData.token || contextData.user.access_token || "");
            console.log(response);
            showToast(t("LegalResearchForwardedToDirector"), "success");
            setData(response);
            setOpenConfirmModal(false);
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegalResearchRequest"), "error");
        } finally {
            setConfirmLoading(false);
        }
    }

    const handleForwardToProsecutor = async () => {
        setConfirmLoading(true);

        const updateData = new FormData();
        updateData.append("forward_status", LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value);

        try {
            const response = await updateLegalResearchRequestCommittee(data.id, updateData, contextData.token || contextData.user.access_token || "");
            console.log(response);
            showToast(t("LegalResearchForwardedToProsecutor"), "success");
            setData(response);
            setOpenConfirmModal(false);
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegalResearchRequest"), "error");
        } finally {
            setConfirmLoading(false);
        }
    }

    const handleCoordinatorForward = async () => {
        setConfirmLoading(true);

        const updateData = new FormData();
        updateData.append("forward_status", LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value);

        try {
            const response = await updateLegalResearchRequestCommittee(data.id, updateData, contextData.token || contextData.user.access_token || "");
            console.log(response);
            showToast(t("LegalResearchForwardedToProsecutor"), "success");
            setData(response);
            setOpenConfirmModal(false);
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegalResearchRequest"), "error");
        } finally {
            setConfirmLoading(false);
        }
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setSubmitting(true);

        const formData = new FormData();
        for (const key in formState) {
            const value = formState[key as keyof typeof formState];
            if (value !== null) {
                if (value instanceof File) {
                    formData.append(key, value);
                } else {
                    formData.append(key, String(value));
                }
            }
        }

        try {
            await createLegalResearchRequest(contextData.token, formData);
            showToast(t("LegalResearchRequestSentSuccess"), "success");
            setFormState({
                reason: "",
                letter_reference_number: "",
                request_summary: "",
                research_topic: "",
                request_letter: null,
                draft_instruction: null,
                respondbacks: null,
            });
            if (fileInputRefRequestLetter.current)
                fileInputRefRequestLetter.current.value = "";
            if (fileInputRefDraftInstruction.current)
                fileInputRefDraftInstruction.current.value = "";
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToSendLegalResearchRequest"), "error");
        } finally {
            setSubmitting(false);
        }
    };

    if (data.isLoading)
        return (
            <div className="flex justify-center items-center h-64">
                {LoadingPageIcon}
            </div>
        );

    if (contextData?.user?.roles?.some((p) => p === "GeneralAttorney") || contextData?.user?.roles?.some((p) => p === "OAGBManagementHead") ||
        contextData?.user?.roles?.some((p) => p === "LRDIFUDeputy"))
        return <HeadOfOAGBLegalRequest request={data} />;

    if (contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector"))
        return <DirectorLegalResearch request={data} />;

    if (contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator"))
        return <CoordinatorConductLegalResearch request={data} />;

    if (expandRoles(contextData?.user?.roles || []).some((p) => p === "LegalResearchProsecutor"))
        return <ProsecutorLegalResearch request={data} />;
        
    if (contextData?.user?.roles?.some((p) => p === "Registrar"))
        return <RegistrarLegalResearch request={data} />

    return (
        <div>
            <PageTitle
                icon="⚖"
                text={t("RequestForConductingLegalResearch")}
                subtitle={t("LegalResearchSubtitle")}
                iconColor="text-purple-600"
                textColor="text-[#432101]"
            />
            <form onSubmit={handleSubmit}>
                <TwoColumnLayout
                    left={
                        <div className="flex flex-col">
                            {data.forward_status === LEGAL_RESEARCH_REQUEST_STATUS.not_forwarded.value &&
                                data.status === LEGAL_RESEARCH_REQUEST_STATUS.pending.value &&
                                <button type="button" onClick={() => { setPendingAction('forward'); setOpenConfirmModal(true); }} className="ml-auto bg-black text-white px-6 py-3 rounded-xl font-medium shadow-lg hover:shadow-xl hover:bg-gray-800 transition-all duration-200 mb-4">{t("SubmitToOAGB")}</button>
                            }
                            {data.status === LEGAL_RESEARCH_REQUEST_STATUS.research_committee_respondbacked.value &&
                                data.forward_status === LEGAL_RESEARCH_REQUEST_STATUS.not_forwarded.value &&
                                <button type="button" onClick={() => { setPendingAction('forward'); setOpenConfirmModal(true); }} className="ml-auto bg-black text-white px-6 py-3 rounded-xl font-medium shadow-lg hover:shadow-xl hover:bg-gray-800 transition-all duration-200 mb-4">{t("Forward")}</button>
                            }
                            {data.forward_status === LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value &&
                                data.status === LEGAL_RESEARCH_REQUEST_STATUS.research_committee_acceptd.value &&
                                <button type="button" onClick={() => { setPendingAction('coordinatorForward'); setOpenConfirmModal(true); }} className="ml-auto bg-black text-white px-6 py-3 rounded-xl font-medium shadow-lg hover:shadow-xl hover:bg-gray-800 transition-all duration-200 mb-4">{t("ForwardToProsecutor")}</button>
                            }
                            {data.status === LEGAL_RESEARCH_REQUEST_STATUS.research_underway.value &&
                                data.forward_status !== LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value &&
                                <button type="button" onClick={() => { setPendingAction('forwardToProsecutor'); setOpenConfirmModal(true); }} className="ml-auto bg-black text-white px-6 py-3 rounded-xl font-medium shadow-lg hover:shadow-xl hover:bg-gray-800 transition-all duration-200 mb-4">{t("ForwardToProsecutor")}</button>
                            }
                            <div>
                                {data.research_completion_letter_file &&
                                    <>
                                        <div className="flex mb-4">
                                            <p className="font-semibold text-base mb-1">{t("RegistrarLetterReferenceNumber")}:</p>
                                            <p className="font-semibold text-base mb-1">{data.letter_reference_number}</p>
                                        </div>
                                        <div className="max-w-4xl bg-white rounded-lg p-6 border border-gray-400 mb-4 h-fit">
                                            <p className="font-semibold text-base mb-1">{t("MessengerDetail")}</p>
                                            <p className="text-[#B8B8B8] font-regular text-sm mb-1">{data?.messenger_name}</p>
                                            <div className="flex mt-4">
                                                <FileView title={t("ResearchCompletionLetterFile")} file={data?.research_completion_letter_file} />
                                            </div>
                                        </div>
                                    </>
                                }
                                <Notes
                                    title={t("CommitteeRequestSummary")}
                                    text={data?.request_summary ?? ""}
                                    subtext={formatDate(data.date ?? "")}
                                    topCorner={
                                        <div className="flex justify-end mb-4 gap-2">
                                            <StatusChip status={data?.status || ""} />
                                            {(data.forward_status === LEGAL_RESEARCH_REQUEST_STATUS.not_forwarded.value ||
                                                data.status === LEGAL_RESEARCH_REQUEST_STATUS.research_committee_respondbacked.value) &&
                                                <div className="flex gap-2">
                                                    <button onClick={() => navigate("/conduct-legal-research/committe-request", {
                                                        state: { request: data },
                                                    })} type="button" className="ml-auto hover:bg-gray-200 text-gray-500 bg-white rounded-lg my-auto px-2">
                                                        <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="black" />
                                                        </svg>
                                                    </button>
                                                    {(data.forward_status === LEGAL_RESEARCH_REQUEST_STATUS.not_forwarded.value &&
                                                        data.status === LEGAL_RESEARCH_REQUEST_STATUS.responded.value) &&
                                                        <button onClick={() => (console.log("delete hehe"))} type="button" className="ml-auto hover:bg-gray-200 text-gray-500 bg-white rounded-lg my-auto px-2">
                                                            <svg width="24" height="24" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M10 2.5C10.1326 2.5 10.2598 2.55268 10.3536 2.64645C10.4473 2.74021 10.5 2.86739 10.5 3C10.5 3.13261 10.4473 3.25979 10.3536 3.35355C10.2598 3.44732 10.1326 3.5 10 3.5H9.5L9.4985 3.5355L9.032 10.071C9.01404 10.3233 8.90115 10.5594 8.71606 10.7318C8.53097 10.9042 8.28743 11 8.0345 11H3.965C3.71207 11 3.46853 10.9042 3.28344 10.7318C3.09835 10.5594 2.98546 10.3233 2.9675 10.071L2.501 3.536L2.5 3.5H2C1.86739 3.5 1.74021 3.44732 1.64645 3.35355C1.55268 3.25979 1.5 3.13261 1.5 3C1.5 2.86739 1.55268 2.74021 1.64645 2.64645C1.74021 2.55268 1.86739 2.5 2 2.5H10ZM7 1C7.13261 1 7.25979 1.05268 7.35355 1.14645C7.44732 1.24021 7.5 1.36739 7.5 1.5C7.5 1.63261 7.44732 1.75979 7.35355 1.85355C7.25979 1.94732 7.13261 2 7 2H5C4.86739 2 4.74021 1.94732 4.64645 1.85355C4.55268 1.75979 4.5 1.63261 4.5 1.5C4.5 1.36739 4.55268 1.24021 4.64645 1.14645C4.74021 1.05268 4.86739 1 5 1H7Z" fill="#9B0505" />
                                                            </svg>
                                                        </button>
                                                    }
                                                </div>
                                            }
                                        </div>
                                    }
                                />
                                <br />
                                {
                                    data?.remark_for_stakeholder && (
                                        <>
                                            <Notes
                                                title={t("HeadOfOAGBResponseFeedback")}
                                                text={data?.remark_for_stakeholder ?? ""}
                                            />
                                            <br />
                                        </>
                                    )}
                                <ResearchTopicCard legalResearch={data} />
                            </div>
                        </div>
                    }
                    right={
                        <div className="w-full max-w-md space-y-4">
                            <OfficialFeedbackCard
                                feedbacks={data.respondbacks}
                                feedbackType="stakeholder_request"
                            />
                            <div className="bg-white rounded-xl shadow-sm p-4 space-y-4">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <h2 className="font-semibold">{t("DocumentRequirements")}</h2>
                                        <p className="text-sm text-gray-500">
                                            {t("DocumentRequirementsDescription")}
                                        </p>
                                    </div>
                                </div>
                                <div className="border-t pt-2 space-y-2">
                                    <div className="flex justify-between items-center p-2 hover:bg-gray-100 rounded-md cursor-pointer">
                                        <span className="text-sm font-medium">
                                            {t("ConductingLegalResearch")}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center p-2 hover:bg-gray-100 rounded-md cursor-pointer">
                                        <span className="text-sm font-medium">
                                            {t("LegislativeDrafting")}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                />
            </form>

            <ConfirmationModal
                isOpen={openConfirmModal}
                title={t("ConfirmAction")}
                confirmText={t("YesContinue")}
                cancelText={t("Cancel")}
                isLoading={confirmLoading}
                onCancel={() => { setOpenConfirmModal(false); setPendingAction(null); }}
                onConfirm={() => {
                    if (pendingAction === 'forward') {
                        handleForwardStatus();
                    } else if (pendingAction === 'forwardToProsecutor') {
                        handleForwardToProsecutor();
                    } else if (pendingAction === 'coordinatorForward') {
                        handleCoordinatorForward();
                    }
                    setPendingAction(null);
                }}
            />
            <AssignProsecutorModal
                data={data}
                setLegalResearch={setData}
                isOpen={isAssignProsecutorModalOpen}
                onClose={() => setIsAssignProsecutorModalOpen(false)}
            />
        </div>
    );
};

export default SingleCommitteeConductLegalResearch;